import React from 'react';
import { object, number, func, bool, string, array } from 'prop-types';
import styled from 'styled-components';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import FlightCard from '../FlightCard';
import Wrapper from '../Wrapper';
import Text from '../Text/Text';
import Row from '../Container/Row';
import Column from '../Container/Column';
import LOBIcon from '../Icon';

import TwoLineTripSummary from '../FlightSummaryCard/TripSummary/TwoLineTripSummary';
import HotelCardSummary from './HotelCardSummary';
import HotelCardNewSummary from './HotelCardNewSummary';

const Icon = styled(LOBIcon)`
  margin-right: 6px;
  padding-top: 3px;
  cursor: pointer;
`;

const RotatedIcon = styled(Icon)`
  transform: rotate(90deg);
`;

const FlightWrapper = styled(Column)`
  background-color: ${({ theme }) => theme.colors.greyLighter};
  border-radius: 4px;
`;
const NewFlightWrapper = styled(Column)`
  border-radius: 4px;
  margin-bottom: 16px;
`;

const HotelWrapper = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.greyLighter};
`;

//TODO: to change flight icon
const TripSummary = ({
  rooms,
  flights: {
    origin,
    destination,
    fromDate,
    toDate,
    departureFlight,
    arrivalFlight,
    isShowFerryAndBus,
  },
  hotelSummary: {
    name,
    fromDate: hotelFromDate,
    toDate: hotelToDate,
    fromDateHotel = '',
    toDateHotel = '',
    image,
    roomsDescription,
    destination: hotelDestination,
    nights,
  },
  onImageLoad,
  isSnapOMSFlightsWeb,
  move,
  enableNewHotelSummaryWeb,
  bedGroups,
  rateAmenitiesForSummary,
  onClick,
  enableNewFlightSummaryGuestWeb,
  onChangeFlight,
  handleIconClick,
}) => {
  const FlightType = {
    Departure: 'departure',
    Arrival: 'arrival',
  };
  const props = {
    departLabel: translate('tripSummary.depart'),
    returnLabel: translate('tripSummary.return'),
    departure: departureFlight,
    arrival: arrivalFlight,
    carrierLogo: `https://static.airasia.com/flights/images/airlines/logos/64/${departureFlight?.flightDetails?.segments[0]?.marketingCarrierCode}.png`,
    carrierLogo1: `https://static.airasia.com/flights/images/airlines/logos/64/${departureFlight?.flightDetails?.segments[1]?.marketingCarrierCode}.png`,
    carrierLogo2: `https://static.airasia.com/flights/images/airlines/logos/64/${departureFlight?.flightDetails?.segments[2]?.marketingCarrierCode}.png`,
    returnCarrierLogo: `https://static.airasia.com/flights/images/airlines/logos/64/${arrivalFlight?.flightDetails?.segments[0]?.marketingCarrierCode}.png`,
    returnCarrierLogo1: `https://static.airasia.com/flights/images/airlines/logos/64/${arrivalFlight?.flightDetails?.segments[1]?.marketingCarrierCode}.png`,
    returnCarrierLogo2: `https://static.airasia.com/flights/images/airlines/logos/64/${arrivalFlight?.flightDetails?.segments[2]?.marketingCarrierCode}.png`,
    changeFlight: () => {
      onChangeFlight({
        flight: departureFlight,
        flightType: FlightType.Departure,
      });
    },
    title: translate('tripSummary.flightSummaryTitle'),
    changeFlightLabel: translate('common.changeFlight'),
    airlineNameDepart:
      departureFlight?.flightDetails?.segments[0]?.marketingAirlineName,
    airlineNameReturn:
      arrivalFlight?.flightDetails?.segments[0]?.marketingAirlineName,
    origin: origin,
    destination: destination,
    handleIconClick,
  };
  return (
    <>
      {enableNewFlightSummaryGuestWeb ? (
        <NewFlightWrapper id="new-flight-details">
          <TwoLineTripSummary {...props} />
        </NewFlightWrapper>
      ) : (
        <>
          <Row mb={[4, 3]} pl={[4, 0]}>
            <RotatedIcon icon="AirplaneModeActiveIcon" fontsize="normal" />
            <Text id="roundtrip-flight-title">
              {translate('tripSummary.roundTripFlight')}
            </Text>
          </Row>
          <FlightWrapper id="flight-details">
            <Wrapper id="departure-flight">
              <FlightCard
                origin={origin}
                destination={destination}
                departDate={fromDate}
                isShowStopsInfo={isShowFerryAndBus}
                {...departureFlight}
                travel={true}
                airlineCode={departureFlight.airlineCode}
                isSnapOMSFlightsWeb={isSnapOMSFlightsWeb}
                move={move}
              />
            </Wrapper>
            <Wrapper id="return-flight">
              <FlightCard
                origin={destination}
                destination={origin}
                departDate={toDate}
                isShowStopsInfo={isShowFerryAndBus}
                {...arrivalFlight}
                travel={false}
                airlineCode={arrivalFlight.airlineCode}
                isSnapOMSFlightsWeb={isSnapOMSFlightsWeb}
                move={move}
              />
            </Wrapper>
          </FlightWrapper>
        </>
      )}
      {!enableNewHotelSummaryWeb && (
        <Row mb={[4, 3]} mt={[5, 4]} pl={[4, 0]}>
          <Icon icon="HotelIcon" fontsize="normal" />
          <Text fontSize={16} id="hotel-details-title">
            {translate('tripSummary.hotelIn', { place: hotelDestination })}
          </Text>
        </Row>
      )}
      {enableNewHotelSummaryWeb && (
        <HotelWrapper>
          <HotelCardNewSummary
            roomsCount={rooms}
            className="hotel-summary"
            hotelName={name}
            fromDate={fromDateHotel || hotelFromDate}
            toDate={toDateHotel || hotelToDate}
            imageUrl={image}
            roomsDescription={roomsDescription}
            totalNights={nights}
            onImageLoad={onImageLoad}
            move={move}
            bedGroups={bedGroups}
            rateAmenitiesForSummary={rateAmenitiesForSummary}
            onClick={onClick}
          />
        </HotelWrapper>
      )}
      {!enableNewHotelSummaryWeb && (
        <HotelCardSummary
          roomsCount={rooms}
          className="hotel-summary"
          hotelName={name}
          fromDate={fromDateHotel || hotelFromDate}
          toDate={toDateHotel || hotelToDate}
          imageUrl={image}
          roomsDescription={roomsDescription}
          totalNights={nights}
          onImageLoad={onImageLoad}
          move={move}
        />
      )}
    </>
  );
};

TripSummary.propTypes = {
  flights: object.isRequired,
  hotelSummary: object.isRequired,
  rooms: number,
  onImageLoad: func,
  isSnapOMSFlightsWeb: bool,
  move: bool,
  enableNewHotelSummaryWeb: bool,
  bedGroups: string,
  rateAmenitiesForSummary: array,
  onClick: func,
  enableNewFlightSummaryGuestWeb: bool,
  onChangeFlight: func,
  handleIconClick: func,
};

export default TripSummary;
